import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { ReactComponent as GraphLogoIcon } from './assets/logo.svg';
import { ReactComponent as DownloadIcon } from './assets/export.svg';
import { ReactComponent as HelpCircleIcon } from './assets/help.svg';
import { ReactComponent as  SvgFormatIcon } from './assets/file.svg';
import { ReactComponent as PngFormatIcon } from './assets/picture.svg';
import { ReactComponent as ChevronDownIcon } from './assets/chevron.svg';
import { ReactComponent as ExportIcon } from './assets/download.svg';
import { useMenuDismiss } from './useMenuDismiss';
import { GraphHostActions } from './hostActions';

import styles from './SocialGraphPOC.module.scss';


// tooltip anchor for the Z-score report button's disabled explanation
const HEADER_HINT_TOOLTIP_ID = 'socialGraphHeaderHint';

type GraphHeaderProps = {
  title: string;
  actions: GraphHostActions | null;
  onZScoreReport?: () => void;
  // hides the Z-score report button entirely (graphs with no Z-score data)
  zscoreReportHidden?: boolean;
  // renders the button disabled with this explanation as a hover/focus tooltip
  zscoreReportDisabledReason?: string;
  onExportPng?: () => void;
  onExportSvg?: () => void;
  onGraphGuide?: () => void;
};

export const GraphHeader: React.FC<GraphHeaderProps> = ({
  title,
  actions,
  onZScoreReport,
  zscoreReportHidden,
  zscoreReportDisabledReason,
  onExportPng,
  onExportSvg,
  onGraphGuide,
}) => {
  const { t } = useTranslation();
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  const exportTriggerRef = useRef<HTMLButtonElement>(null);
  const canExport = Boolean(onExportPng || onExportSvg);

  useMenuDismiss(exportOpen, exportRef, exportTriggerRef, () => setExportOpen(false));

  // if export becomes unavailable while the menu is open (graph cleared), both
  // items go disabled — close instead of leaving a dangling unusable menu
  useEffect(() => {
    if (!canExport) setExportOpen(false);
  }, [canExport]);

  const selectExportFormat = (handler?: () => void) => {
    setExportOpen(false);
    exportTriggerRef.current?.focus();
    handler?.();
  };

  // arrow-key navigation between menu items, per WAI-ARIA menu button pattern
  const focusMenuItem = (fromIndex: number, direction: 1 | -1) => {
    const items = exportRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    if (!items || items.length === 0) return;
    const nextIndex = (fromIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  return (
    // a plain div, not a semantic <header> — App.scss has a global bare
    // `header { position: fixed; left: 345px; ... }` rule for the app shell's
    // page header that would hijack this one (position/size/left all wrong)
    <div className={styles.graphHeader}>
      <div className={styles.graphHeaderLeft}>
        {actions ? (
          <button
            type="button"
            className={styles.graphHeaderLogo}
            onClick={actions.openGraphHome}
            aria-label={t('Social graph home')}
          >
            <GraphLogoIcon aria-hidden="true" />
          </button>
        ) : (
          <div className={styles.graphHeaderLogo} aria-hidden="true">
            <GraphLogoIcon />
          </div>
        )}
        <h1 className={styles.graphHeaderTitle}>{title}</h1>
      </div>
      <div className={styles.graphHeaderRight}>
        {!zscoreReportHidden && (
          // the tooltip anchor is a wrapper span because disabled buttons don't
          // reliably emit the hover events react-tooltip listens for
          <span
            className={styles.graphHeaderBtnWrap}
            data-tooltip-id={zscoreReportDisabledReason ? HEADER_HINT_TOOLTIP_ID : undefined}
            data-tooltip-content={zscoreReportDisabledReason}
          >
            <button
              type="button"
              className={styles.graphHeaderBtn}
              onClick={onZScoreReport}
              disabled={!onZScoreReport || Boolean(zscoreReportDisabledReason)}
            >
              <DownloadIcon />
              {t('Z-score report')}
              {zscoreReportDisabledReason && <span className={styles.srOnly}>{zscoreReportDisabledReason}</span>}
            </button>
          </span>
        )}
        <Tooltip
          id={HEADER_HINT_TOOLTIP_ID}
          className={styles.actorHintTooltip}
          positionStrategy="fixed"
          delayShow={200}
        />
        <div className={styles.graphHeaderExportWrap} ref={exportRef}>
          <button
            type="button"
            ref={exportTriggerRef}
            className={styles.graphHeaderBtn}
            onClick={() => setExportOpen((open) => !open)}
            disabled={!canExport}
            aria-haspopup="menu"
            aria-expanded={exportOpen}
          >
            <ExportIcon />
            {t('Export')}
            <ChevronDownIcon />
          </button>
          {exportOpen && (
            <div className={styles.graphHeaderExportMenu} role="menu" aria-label={t('Export format')}>
              <button
                type="button"
                role="menuitem"
                className={styles.graphHeaderExportItem}
                disabled={!onExportPng}
                onClick={() => selectExportFormat(onExportPng)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') { event.preventDefault(); focusMenuItem(0, 1); }
                  if (event.key === 'ArrowUp') { event.preventDefault(); focusMenuItem(0, -1); }
                }}
              >
                <PngFormatIcon />
                <span className={styles.graphHeaderExportItemLabel}>PNG</span>
                <span className={styles.graphHeaderExportItemDesc}>{t('2× canvas')}</span>
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.graphHeaderExportItem}
                disabled={!onExportSvg}
                onClick={() => selectExportFormat(onExportSvg)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown') { event.preventDefault(); focusMenuItem(1, 1); }
                  if (event.key === 'ArrowUp') { event.preventDefault(); focusMenuItem(1, -1); }
                }}
              >
                <PngFormatIcon />
                <span className={styles.graphHeaderExportItemLabel}>SVG</span>
                <span className={styles.graphHeaderExportItemDesc}>{t('vector')}</span>
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.graphHeaderBtn}
          onClick={onGraphGuide}
          disabled={!onGraphGuide}
        >
          <HelpCircleIcon />
          {t('Graph guide')}
        </button>
        {actions && (
          <>
            <div className={styles.graphHeaderSeparator} aria-hidden="true" />
            <button type="button" className={styles.graphHeaderBtn} onClick={actions.exit}>
              {t('Exit')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
